import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { API_URI } from "../../../constants/GlobalConstants";
import { getCreditData } from "../../../redux/actions/CreditSystemActions";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

export default function PaymentModal({
  open,
  onClose,
  onSave,
  orderId,
  dealerId,
  token,
  selected,
}) {
  const {
    creditData,
    loading: creditDataLoading,
    error,
  } = useSelector((state) => state.credit);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [paymentDate, setPaymentDate] = useState(dayjs());       
  const [remarks, setRemarks] = useState("");                     
  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCreditData(id));
  }, [dispatch]);

  const handleAmountChange = (event) => setAmount(event.target.value);

  const handleModeOfPaymentChange = (event) =>
    setModeOfPayment(event.target.value);

  const handleSave = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0 || !modeOfPayment) {
      onSave({
        success: false,
        message: "Please enter a valid amount and select a mode of payment.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.put(
        `${API_URI}/api/order/update-payment-balance`,
        {
          amount,
          orderIds: selected,
          modeOfPayment,
          dealerId,
          paymentDate: paymentDate.toDate(),    // ← Added
          remarks,                              // ← Added
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      onSave({ success: true, message: response.data.message });

      setAmount("");
      onClose();
    } catch (error) {
      onSave({
        success: false,
        message: error.response ? error.response.data.message : error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    width: "400px",
  };

  useEffect(() => {
    if (!open) {
      setAmount("");
      setModeOfPayment("");
      setPaymentDate(dayjs());    // ← Reset added
      setRemarks("");             // ← Reset added
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={modalStyle}>
        <Typography
          id="modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 2, color: "black" }}
        >
          Receivable
        </Typography>
        <TextField
          label="Amount"
          variant="outlined"
          fullWidth
          margin="normal"
          value={amount}
          onChange={handleAmountChange}
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Mode of Payment</InputLabel>
          <Select
            value={modeOfPayment}
            onChange={handleModeOfPaymentChange}
            label="Mode of Payment"
          >
            <MenuItem value="Check">Cheque</MenuItem>
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Credit Card">Credit Card</MenuItem>
            <MenuItem value="Bank Remittance">Bank Remittance</MenuItem>
            <MenuItem value="ERP Payment">
              ERP Payment (Remaining Credit: ${creditData?.totalCredits})
            </MenuItem>{" "}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label="Date of Payment"
            value={paymentDate}
            onChange={(newValue) => setPaymentDate(newValue)}
            slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
          />
        </LocalizationProvider>

        <TextField
          label="Remarks (Optional)"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="e.g. Cheque #12345"
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}